/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */

import { CommonModule } from '@angular/common';
import {
	Component,
	ChangeDetectionStrategy,
	OnInit,
	OnDestroy,
	Input,
	Optional,
	HostBinding,
	ViewChild,
	ElementRef,
	Self,
	ChangeDetectorRef,
	DoCheck,
} from '@angular/core';
import {
	ControlValueAccessor,
	Validators,
	NgControl,
	ValidatorFn,
	AbstractControl,
	ValidationErrors,
	FormGroupDirective,
	NgForm,
	FormsModule,
	ReactiveFormsModule,
	FormBuilder,
	FormGroup,
	FormControl,
} from '@angular/forms';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
	TpFilterPredicateOp,
	TextPredicateOp,
	ITPFilterPredicateOpPair,
	ITPFilter,
	NumericPredicateOp,
	DropDownPredicateOp,
} from 'src/app/app-data/models/TPFilterPredicates';
import {
	DropDownPredicateOpMap,
	NumericPredicateOpMap,
	TextPredicateOpMap,
} from 'src/app/modules/reports/types/ReportCustomFieldFilterUtils';
import {
	DisambiguationEventTypes,
	UserSystemDisambiguation,
} from 'src/app/plain-javascript/services/UserSystemDisambiguation';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import { MaterialModule } from 'src/app/common/material/material-module';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';

/** Error when invalid control is dirty */
export class MyErrorStateMatcher implements ErrorStateMatcher {
	public isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
		return form && form.invalid && form.dirty;
	}
}

interface ITPFilterForm {
	operator: ITPFilterPredicateOpPair;
	value?: string | number | string[];
}

type FieldType = 'text' | 'number';
// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6226
// type FieldType = 'text' | 'number' | 'dropdown';
// END PLACEHOLDER REPLACE AS PART OF TP-6226

const defaultTextOperator: TpFilterPredicateOp = TextPredicateOp.AnyValue;
const defaultNumericOperator: TpFilterPredicateOp = NumericPredicateOp.AnyValue;
// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6226
// const defaultDropDownOperator: TpFilterPredicateOp = DropDownPredicateOp.AnyValue;
// END PLACEHOLDER REPLACE AS PART OF TP-6226

function TPFilterCompleteValidator(internalGroup: FormGroup): ValidatorFn {
	return (control: AbstractControl): ValidationErrors | null => {
		const value = control.value;
		if (!value) {
			return null;
		}
		return internalGroup.invalid ? { filterIncomplete: true } : null;
	};
}

@Component({
	selector: 'tp-filter',
	templateUrl: './tp-filter.component.html',
	styleUrls: ['./tp-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{ provide: ErrorStateMatcher, useClass: MyErrorStateMatcher },
		{ provide: MatFormFieldControl, useExisting: TPFilterComponent },
	],
	standalone: true,
	imports: [CommonModule, MaterialModule, FormsModule, ReactiveFormsModule],
})
export class TPFilterComponent
	implements DoCheck, OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<ITPFilterForm>
{
	constructor(
		@Self() @Optional() public ngControl: NgControl,
		private formBuilder: FormBuilder,
		@Optional() private controlContainer: FormGroupDirective,
		private changeDetectorRef: ChangeDetectorRef
	) {
		if (this.ngControl) {
			this.ngControl.valueAccessor = this;
		}
	}

	//to reverse execution order
	//https://github.com/angular/angular/issues/29218
	private angularBugPatchStuff = {
		lastWriteValueArgs: null,
		initHasRan: () => this.internalTPFilter !== undefined,
	};

	@ViewChild('inputField') private inputField: ElementRef;

	private defaultOperator: ITPFilterPredicateOpPair;

	/**
	 * The field type of the filter.
	 * Sets the default operator based on the specified fieldType.
	 *
	 * @remarks
	 * **Note:** This updates the UI with the proper operator options and field based on the fieldType.
	 * However, it does not emit the updated value of the operator and value fields if they change,
	 * to remain consistent with the default Angular behavior.
	 */
	@Input() set fieldType(value: FieldType) {
		switch (value) {
			case 'text':
				this.defaultOperator = {
					key: defaultTextOperator,
					label: TextPredicateOpMap.get(defaultTextOperator as TextPredicateOp),
				};
				break;
			case 'number':
				this.defaultOperator = {
					key: defaultNumericOperator,
					label: NumericPredicateOpMap.get(defaultNumericOperator as NumericPredicateOp),
				};
				break;
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6226
			// case 'dropdown':
			// 	this.defaultOperator = {
			// 		key: defaultDropDownOperator,
			// 		label: DropDownPredicateOpMap.get(defaultDropDownOperator as DropDownPredicateOp),
			// 	};
			// 	break;
			// END PLACEHOLDER REPLACE AS PART OF TP-6226
			default:
				this.defaultOperator = {
					key: defaultTextOperator,
					label: TextPredicateOpMap.get(defaultTextOperator as TextPredicateOp),
				};
				break;
		}
		this.fieldType$.next(value);
	}

	@Input() public numericTypeOptions: { unit: string };
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6226
	// @Input() public dropdownTypeOptions: { option$: Observable<OptionCollection>; multiSelect: boolean };
	// END PLACEHOLDER REPLACE AS PART OF TP-6226

	@Input() public get placeholder() {
		return this._placeholder;
	}
	public set placeholder(pla: string) {
		this._placeholder = pla;
		this.stateChanges.next();
	}
	private _placeholder = '';

	private excludeOperators$ = new BehaviorSubject<Array<TpFilterPredicateOp>>([]);
	/**
	 * A list of operators to exclude from the filter for the specified `fieldType`.
	 *
	 * This property allows the exclusion of certain operators from the filter logic, ensuring that the specified operators
	 * are not available for selection. The exclusion is persistent, meaning that
	 * if an operator is excluded for one `fieldType`, it will remain excluded if the `fieldType` changes, unless it is explicitly included again.
	 *
	 * For example, if the `NotSet` operator is excluded for `text`, it will also be excluded for `number` unless it is re-included.
	 *
	 * @type TpFilterPredicateOp[]
	 * @default []
	 * @example
	 * <!-- Exclude specific operators for the filter -->
	 * <tp-filter fieldType="text" [excludeOperators]="[TextPredicateOp.NotSet, TextPredicateOp.AnyValue]"></tp-filter>
	 */
	@Input() public set excludeOperators(hideOps: Array<TpFilterPredicateOp>) {
		this.excludeOperators$.next(hideOps);
	}

	public internalModel: UserSystemDisambiguation<ITPFilterForm>;

	private readonly textPredicateOpPairs = Array.from(TextPredicateOpMap.entries(), ([key, label]) => ({
		key,
		label,
	}));
	private readonly numericPredicateOpPairs = Array.from(NumericPredicateOpMap.entries(), ([key, label]) => ({
		key,
		label,
	}));
	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6226
	// private readonly dropDownPredicateOpPairs = Array.from(
	// 	DropDownPredicateOpMap.entries(),
	// 	([key, label]) => ({ key, label })
	// );
	// END PLACEHOLDER REPLACE AS PART OF TP-6226

	public internalTPFilter: FormGroup;

	public chipOperator$ = new BehaviorSubject<ITPFilterPredicateOpPair[]>(null);

	public fieldType$ = new BehaviorSubject<string>(null);

	private shutdownSubject$ = new Subject<void>();

	public ngOnInit(): void {
		this.internalTPFilter = this.formBuilder.group({
			operator: [undefined, Validators.required],
			value: [undefined, Validators.required],
		});

		this.internalModel = new UserSystemDisambiguation(this.internalTPFilter);

		if (this.ngControl) {
			this.ngControl.control.addValidators([TPFilterCompleteValidator(this.internalTPFilter)]);
			this.ngControl.control.updateValueAndValidity({ emitEvent: false });
		}

		combineLatest([this.fieldType$, this.excludeOperators$])
			.pipe(
				map(([fieldType, excludeOperators]) => {
					switch (fieldType) {
						case 'text':
							return this.textPredicateOpPairs.filter((pair) => !excludeOperators.includes(pair.key));
						case 'number':
							return this.numericPredicateOpPairs.filter((pair) => !excludeOperators.includes(pair.key));
						// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6226
						// case 'dropdown':
						// 	return this.dropDownPredicateOpPairs;
						// END PLACEHOLDER REPLACE AS PART OF TP-6226
						default:
							return null;
					}
				}),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(this.chipOperator$);

		this.initUserInputListeners();

		//set disabled state if operator changes
		this.internalModel.dataChangeRaw$.pipe(takeUntil(this.shutdownSubject$)).subscribe((dataChange) => {
			const operator = dataChange.data.operator;

			const control = this.internalTPFilter.get('value');
			if (!operator) {
				return;
			} //initialization nonsense

			if (operator.key === TextPredicateOp.AnyValue || operator.key === TextPredicateOp.NotSet) {
				if (!control.disabled) {
					control.disable({ emitEvent: dataChange.emissionType === DisambiguationEventTypes.UserChange });
				}
			} else {
				if (!control.enabled) {
					control.enable({ emitEvent: dataChange.emissionType === DisambiguationEventTypes.UserChange });
				}
			}
		});

		if (this.angularBugPatchStuff.lastWriteValueArgs) {
			this.writeValue(this.angularBugPatchStuff.lastWriteValueArgs);
			this.angularBugPatchStuff.lastWriteValueArgs = null;
			if (this.ngControl) {
				this.ngControl.control.updateValueAndValidity({ emitEvent: false });
			}
		}

		this.setupMatFormFieldListeners();
	}

	public ngOnDestroy(): void {
		this.fieldType$.complete();
		this.excludeOperators$.complete();
		this.stateChanges.complete();
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
		this.internalModel.destroy();
	}

	ngDoCheck() {
		if (this.ngControl) {
			this.updateErrorState();
		}
	}

	// this relies on some specific timing, so take care when modifying
	// - child input's emit first, which allows us to safely update the rest of the form and be confident that
	// the whole form listener will emit with the unemitted changes
	private initUserInputListeners() {
		//reset subfields on typechange
		// value -> null
		// operator -> Default for the type
		combineLatest([this.fieldType$, this.excludeOperators$])
			.pipe(takeUntil(this.shutdownSubject$))
			.subscribe(() => {
				// We internally set the value to null and the operator to the type default when the fieldType changes.
				// However, the default Angular behavior does NOT emit any change and this is consistent with that defualt behavior. The UI updates
				// (with new mat-chips and a new empty input), but the external control retains the old value.
				this.internalModel.patchValue({
					value: null,
					operator: this.defaultOperator,
				});
				this.onTouched();
			});

		//this will emit last
		this.internalModel.userChange$.pipe(takeUntil(this.shutdownSubject$)).subscribe(() => {
			for (const control of Object.values(this.internalTPFilter.controls)) {
				control.markAsDirty();
			}
			this.outputFormData();
		});
	}

	// this allows for binding "complex data" to the options list, one value for display and one for comparisions.
	// This function does that alternate equivalency checks on just emum’s key value (the whole tuple won’t always pass an equivalency check).
	public chipComparator(optionFromChipList: ITPFilterPredicateOpPair, selection: ITPFilterPredicateOpPair) {
		if (!selection) {
			return false;
		}
		return optionFromChipList.key === selection.key;
	}

	private onValueChange = (thing: any) => {};
	public onTouched = () => {};

	// This will emit regardless of validity, which with then trigger a recalc of the validator
	private outputFormData() {
		const externalFormData = this.convertInternalToExternal(this.internalTPFilter.value);
		this.onValueChange(externalFormData);
	}

	public writeValue(value: ITPFilter) {
		if (!this.angularBugPatchStuff.initHasRan()) {
			// If the control of this component is initialized without a value (i.e., the value is undefined or null),
			// and the writeValue function is called before the ngOnInit lifecycle hook, the value will be temporarily stored
			// in lastWriteValueArgs. During ngOnInit, this value will be assigned to the control. However, if the value is
			// null or undefined, it will not pass the ngOnInit check. To prevent this, if the value
			// is null or undefined, it will be set to an empty object ({}) here.
			this.angularBugPatchStuff.lastWriteValueArgs = value || {};
			return;
		}

		//write operator and value
		const internalVal = this.convertExternalToInternalPartial(value);

		this.internalModel.patchValue(internalVal);
	}

	private convertInternalToExternal(formData: ITPFilterForm): ITPFilter | null {
		if (!formData) {
			return null;
		}

		return {
			operator: formData.operator?.key,
			value: formData.value ?? null, // replace undefined with null
		};
	}

	private convertExternalToInternalPartial(formData?: ITPFilter): ITPFilterForm {
		//undefined means "reset to undefined"
		if (!formData) {
			return { operator: undefined, value: undefined };
		}

		//set a default operator if one is not provided
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6606
		// enhance this check to not just null check, but check for semantically invalid versions
		// like "GreaterThan" for a string
		const externalOperator = formData.operator || this.defaultOperator.key;

		//tries to get a value from all three maps
		//this _will_ break if we change the NotSet string values to be different by type
		//but otherwise doesn't matter. Change at that time
		const operationStringValue =
			TextPredicateOpMap.get(externalOperator as TextPredicateOp) ||
			NumericPredicateOpMap.get(externalOperator as NumericPredicateOp) ||
			DropDownPredicateOpMap.get(externalOperator as DropDownPredicateOp);

		return {
			operator: { key: externalOperator, label: operationStringValue },
			value: formData.value,
		};
		// END PLACEHOLDER REPLACE AS PART OF TP-6606
	}

	public registerOnChange(fn: (value: any) => void) {
		this.onValueChange = fn;
	}

	/**
	 * ControlValueAccessor function:
	 * This is another method that registers a callback function.  This one needs to be called when this
	 * component is "touched".  We track that via the "(blur)" event handler in the HTML template, which
	 * calls the "onBlur()" method.  That in turn calls the callback method provided as a parameter here.
	 */
	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean) {
		// ngOnInitHasn't been run yet (see commentary on this.angularBugPatchStuff above)
		// if necessary this can be added to that patch, but it's probably not necessary for this to run
		// the internal default should be fine.
		if (!this.angularBugPatchStuff.initHasRan()) {
			return;
		}
		isDisabled
			? this.internalTPFilter.disable({ emitEvent: false })
			: this.internalTPFilter.enable({ emitEvent: false });
		this.stateChanges.next();
	}

	// MatFormFieldControl Implementation
	public get value(): ITPFilterForm | null {
		return this.internalTPFilter.value;
	}

	public stateChanges = new Subject<void>();

	private setupMatFormFieldListeners() {
		this.internalTPFilter.valueChanges
			.pipe(
				map(() => undefined),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.stateChanges));
	}

	static nextId = 0;
	@HostBinding() id = `tp-filter-${TPFilterComponent.nextId++}`;

	public focused = false;

	public onFocusIn() {
		if (!this.focused) {
			this.focused = true;
			this.stateChanges.next();
		}
	}

	public onFocusOut(event: FocusEvent) {
		if (!this.inputField?.nativeElement?.contains(event.relatedTarget as Element)) {
			this.focused = false;
			this.onTouched();
			this.stateChanges.next();
		}
	}

	public get empty() {
		return !this.inputField?.nativeElement?.value;
	}

	@HostBinding('class.floating')
	public get shouldLabelFloat() {
		// If the input is disabled, we should float the label only if the input is not empty
		// If the input is not disabled, we should float the label if the input is focused or not empty
		return this.disabled ? !this.empty : this.focused || !this.empty;
	}

	public get required() {
		return this.internalTPFilter?.get('value')?.hasValidator(Validators.required) ?? false;
	}

	// public disabled = false;
	public get disabled() {
		return this.internalTPFilter.disabled;
	}

	errorState: boolean = false;

	private updateErrorState() {
		const newState =
			this.internalTPFilter.invalid || (this.ngControl?.invalid && this.controlContainer.submitted);

		if (this.errorState !== newState) {
			this.errorState = newState;
			this.stateChanges.next();
		}
	}

	public controlType = 'tp-filter';

	public setDescribedByIds(ids: string[]): void {
		//accessibility stuff
	}

	public onContainerClick(event: MouseEvent): void {
		if ((event.target as Element).tagName.toLowerCase() != 'input') {
			this.inputField.nativeElement.focus();
		}
	}
}
